import React from 'react';
import { Loader2, Check, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  className,
  children,
  isLoading,
  isSuccess,
  isError,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  disabled,
  ...props
}) => {
  const { t } = useLanguage();
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 border border-transparent shadow-sm',
    secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500 border border-transparent shadow-sm',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500',
    ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 border border-transparent shadow-sm',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  let stateStyles = '';
  if (isSuccess) {
    stateStyles = 'bg-green-600 hover:bg-green-700 text-white border-transparent ring-green-500';
  } else if (isError) {
    stateStyles = 'bg-red-600 hover:bg-red-700 text-white border-transparent ring-red-500 animate-shake';
  }

  return (
    <button
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        stateStyles,
        className
      )}
      disabled={isLoading || isSuccess || isError || disabled}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {children}
        </>
      ) : isSuccess ? (
        <>
          <Check className="mr-2 h-5 w-5 animate-scale-in" />
          {t('auth.common.success')}
        </>
      ) : isError ? (
        <>
          <X className="mr-2 h-5 w-5 animate-scale-in" />
          {t('auth.common.failed')}
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};
